import { string, object, mixed, array } from 'yup';
import { insuranceStatuses } from '@becollective/constants';

import { generateEdges } from 'utils/schema';
import GROUP_TYPES, { INDUSTRY_WITH_TYPES } from 'data/groupTypes';

const initInsurancesSchema = () => {
  return array().of(object().shape({
    insuranceType: string(),
    vendor: string()
      .nullable()
      .when(['policyNumber', 'value', 'expiryDate'], {
        is: (policyNumber, value, expiryDate) => {
          return policyNumber || value || expiryDate;
        },
        then: string().required('Insurer name is a required field'),
      }),
    policyNumber: string()
      .nullable()
      .when(['vendor', 'value', 'expiryDate'], {
        is: (vendor, value, expiryDate) => {
          return vendor || value || expiryDate;
        },
        then: string().required('Policy number is a required field'),
      }),
    expiryDate: string()
      .nullable()
      .when(['vendor', 'policyNumber', 'value'], {
        is: (vendor, policyNumber, value) => {
          return vendor || policyNumber || value;
        },
        then: string().required('Insurance expiry date is a required field'),
      }),
    value: string()
      .nullable()
      .when(
        ['vendor', 'policyNumber', 'expiryDate'],
        {
          is: (vendor, policyNumber, expiryDate) => {
            return vendor || policyNumber || expiryDate;
          },
          then: string().required('Insured value is a required field'),
        }
      ),
  }, generateEdges(['vendor', 'policyNumber', 'expiryDate', 'value'])));
};

const schema = object().shape({
  name:
    string()
      .required('Please enter a group name'),
  summary:
    string()
      .required('Please enter the group description'),
  type:
    mixed()
      .oneOf(GROUP_TYPES.map(t=>t.value))
      .required('Please choose a group type'),
  industry:
    string()
      .nullable()
      .when('type', {
        is: (type) => INDUSTRY_WITH_TYPES.includes(type),
        then: string().required('The group type you have selected requires an industry type'),
      }),
  avatarUrl: string().nullable(),
  location:
    object()
      .when('insuranceStatus', {
        is: insuranceStatuses.INSURED.value,
        then: object().shape({
          address: string().nullable(),
          locality: object().nullable(),
          region: object().nullable(),
          country: object().shape({
            long: string(),
            short: string(),
          }).required(''),
          geo: object().nullable(),
        }).required(''),
        otherwise: object().nullable(),
      }),
  insuranceStatus:
    string()
      .oneOf(Object.keys(insuranceStatuses).map(key => (insuranceStatuses[key].value)))
      .required(),
  insurances:
    array()
      .nullable()
      .when('insuranceStatus', {
        is: insuranceStatuses.INSURED.value,
        then: initInsurancesSchema(),
      })
},
  // NOTE: You must enter the 'when' parameters here when you have sibling values
  // that reference each other or you will cause a cyclic dependency error.
  // NOTE: We don't have to enter insuranceStatus, as insuranceStatus does not have
  // a when clause that references the other values.
  // https://github.com/jquense/yup/issues/176#issuecomment-369925782
  [
    ...generateEdges([
      'insuranceStatus',
      'insurances',
      'location',
    ]),
  ],
);

export default schema;
